// src/sections/SearchResultPage.tsx
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaBirthdayCake, FaMapMarkerAlt, FaSearch, FaUser } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

const SearchResultPage: React.FC = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    
    const { phoneNumber, result } = location.state || {};

    if (!phoneNumber) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100">
                <div className="text-center">
                    <h1 className="text-4xl font-bold text-gray-800">No Search Performed</h1>
                    <p className="mt-2 text-lg text-gray-600">Please go back and search for a number.</p>
                    <button
                        onClick={() => navigate('/')}
                        className="mt-6 px-6 py-3 bg-[#AEEA00] text-[#1A237E] rounded-full font-bold hover:bg-lime-600 transition-colors"
                    >
                        Go to Home Page
                    </button>
                </div>
            </div>
        );
    }

    // New navigation function with state
    const handleSearchAnother = () => {
        // Navigate to the home page and pass a state to focus the search bar
        navigate('/', { state: { focusSearch: true } });
    };

    return (
        // The background is now a simple white shade
        <section className="relative w-full flex-1 flex flex-col items-center justify-center text-gray-800 py-16 bg-white">
            <div className="relative z-20 container mx-auto px-6 py-8 md:py-16 flex flex-col items-center justify-center text-center">
                <motion.button
                    onClick={() => navigate('/')}
                    className="absolute top-10 left-10 md:top-20 md:left-20 text-gray-600 hover:text-[#AEEA00] transition-colors duration-200"
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                >
                    <FaArrowLeft className="text-2xl md:text-3xl" />
                </motion.button>

                <motion.h1
                    className="text-4xl md:text-5xl font-extrabold mb-8 leading-tight"
                    initial={{ opacity: 0, y: -50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    {t('Search Result')}
                </motion.h1>

                <motion.div
                    className="w-full max-w-xl bg-gray-100 rounded-3xl p-8 shadow-2xl text-gray-800"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                >
                    <div className="mb-8 w-full max-w-xl">
                        <p className="text-sm font-semibold text-gray-500 mb-2">{t('Searched For Label')}</p>
                        <div className="flex items-center bg-gray-200 rounded-full px-4 py-3 shadow-inner">
                            <FaSearch className="text-xl mr-3 text-lime-700" />
                            <p className="text-lg font-semibold text-gray-800 mr-3">{phoneNumber}</p>
                            <FaMapMarkerAlt className="text-lime-600 text-xl ml-auto" />
                        </div>
                    </div>
                    {result ? (
                        <div className="space-y-6 text-left">
                            <div className="flex items-center text-lime-700 text-2xl font-bold gap-2">
                                <FaUser className="text-[#1A237E]" />
                                <span className="text-sm text-gray-500">({t('Name')})</span><br />
                                <h2 className="text-2xl font-bold">{result.name}</h2>
                            </div>
                            
                            <div className="border-t border-gray-200 pt-6 space-y-4">
                                <div className="flex items-start gap-3">
                                    <FaMapMarkerAlt className="mt-1 text-[#1A237E]" />
                                    <div>
                                        <p className="text-gray-600">{t('Address')}</p>
                                        <p className="text-lg font-medium">{result.address}</p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <FaBirthdayCake className="mt-1 text-[#1A237E]" />
                                    <div>
                                        <p className="text-gray-600">{t('DOB')}</p>
                                        <p className="text-lg font-medium">{result.dob}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center mt-6">
                            <p className="text-lg font-bold text-red-500">
                                {t('User Not Found')}
                            </p>
                            <p className="text-gray-600 mt-2">
                                {t('Check the number or try again.')}
                            </p>
                            {/* NEWLY ADDED BUTTON */}
                            <motion.button
                                onClick={handleSearchAnother}
                                className="mt-4 px-6 py-3 bg-[#AEEA00] text-[#1A237E] rounded-full font-bold hover:bg-lime-600 transition-colors"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.5 }}
                            >
                                Search another number
                            </motion.button>
                            {/* END OF NEW BUTTON */}
                        </div>
                    )}
                </motion.div>
            </div>
        </section>
    );
};

export default SearchResultPage;